import React, { useState, useEffect, useRef, useCallback } from "react";

const InvisibleInk = ({
  children,
  numberOfParticles = 200,
  revealKind = "click",
  visible,
  setVisible,
}) => {
  const [internalIsRevealed, setInternalIsRevealed] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  const isControlled = visible !== undefined && setVisible !== undefined;

  const isRevealed = isControlled ? visible : internalIsRevealed;

  const containerRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  const particlePositionsRef = useRef([]);
  const explosionTimeRef = useRef(0);

  const styles = {
    container: {
      position: "relative",
      width: "100%",
      overflow: "hidden",
      cursor: "pointer",
      borderRadius: "12px",
      padding: "16px",
    },
    content: {
      position: "relative",
      zIndex: 1,
      transition: "filter 0.3s ease, opacity 0.3s ease",
    },
    contentHidden: { filter: "blur(12px)", opacity: 0.2 },
    contentRevealed: { filter: "blur(0)", opacity: 1 },
    canvas: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: 2,
      pointerEvents: "none",
      transition: "opacity 0.5s ease",
    },
  };

  const calculateEdgeOpacity = (x, y, width, height) => {
    const centerX = width / 2;
    const centerY = height / 2;
    const normX = (x - centerX) / centerX / 1.1;
    const normY = (y - centerY) / centerY;
    const distFromCenter = Math.sqrt(normX * normX + normY * normY);
    return Math.pow(Math.max(0, 1 - distFromCenter), 2);
  };

  const resetParticles = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const paddingX = rect.width * 0.08;
    const paddingY = rect.height * 0.12;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    particlesRef.current = [];

    for (let i = 0; i < numberOfParticles; i++) {
      const size = Math.random() * 3 + 1;
      const x = paddingX + Math.random() * (rect.width - 2 * paddingX);
      const y = paddingY + Math.random() * (rect.height - 2 * paddingY);
      const speedX = Math.random() * 1.0 - 0.5;
      const speedY = Math.random() * 1.0 - 0.5;
      const distFromCenter = Math.sqrt(
        Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
      );
      const maxDist =
        Math.sqrt(Math.pow(rect.width, 2) + Math.pow(rect.height, 2)) / 2;
      const normDist = distFromCenter / maxDist;

      particlesRef.current.push({
        x,
        y,
        size,
        speedX,
        speedY,
        originalX: x,
        originalY: y,
        originalSize: size,
        originalSpeedX: speedX,
        originalSpeedY: speedY,
        normalizedDistance: normDist,
        animatingIn: false,
        animationProgress: 0,
        explosionAngle: Math.random() * Math.PI * 2,
        explosionBaseSpeed: 2 + Math.random() * 3,
        explosionMaxSpeed: 5 + Math.random() * 7,
        explosionAcceleration: 0.5 + Math.random() * 0.8,
        currentExplosionSpeed: 0,
        explosionDelay: normDist * 3 + Math.random(),
        explosionStarted: false,
        directionX: 0,
        directionY: 0,
      });
    }
  }, [numberOfParticles]);

  const handleReveal = () => {
    setIsAnimatingIn(false);
    setIsAnimatingOut(true);

    if (isControlled) {
      setVisible(true);
    } else {
      setInternalIsRevealed(true);
    }

    explosionTimeRef.current = 0;

    if (containerRef.current) {
      const { width, height } = containerRef.current;
      const centerX = width / 2;
      const centerY = height / 2;

      particlesRef.current.forEach((p) => {
        const dx = p.x - centerX;
        const dy = p.y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;

        p.directionX = dx / distance;
        p.directionY = dy / distance;
        p.currentExplosionSpeed = p.explosionBaseSpeed;
        p.explosionStarted = false;
        p.animatingIn = false;
      });
    }
  };

  const handleHide = () => {
    if (!isRevealed) return;

    particlePositionsRef.current = particlesRef.current.map((p) => ({
      x: p.x,
      y: p.y,
      size: p.size,
    }));

    if (isControlled) {
      setVisible(false);
    } else {
      setInternalIsRevealed(false);
    }

    setIsAnimatingOut(false);
    setIsAnimatingIn(true);

    particlesRef.current.forEach((p, i) => {
      const current = particlePositionsRef.current[i] || {};
      p.x = current.x || p.x;
      p.y = current.y || p.y;
      p.size = current.size || p.size;
      p.targetX = p.originalX;
      p.targetY = p.originalY;
      p.targetSize = p.originalSize;
      p.animatingIn = true;
      p.animationProgress = 0;
    });
  };

  useEffect(() => {
    if (isControlled) {
      if (visible) {
        setIsAnimatingIn(false);
        setIsAnimatingOut(true);
        explosionTimeRef.current = 0;

        if (containerRef.current) {
          const { width, height } = containerRef.current;
          const centerX = width / 2;
          const centerY = height / 2;

          particlesRef.current.forEach((p) => {
            const dx = p.x - centerX;
            const dy = p.y - centerY;
            const distance = Math.sqrt(dx * dx + dy * dy) || 1;

            p.directionX = dx / distance;
            p.directionY = dy / distance;
            p.currentExplosionSpeed = p.explosionBaseSpeed;
            p.explosionStarted = false;
            p.animatingIn = false;
          });
        }
      } else {
        particlePositionsRef.current = particlesRef.current.map((p) => ({
          x: p.x,
          y: p.y,
          size: p.size,
        }));

        setIsAnimatingOut(false);
        setIsAnimatingIn(true);

        particlesRef.current.forEach((p, i) => {
          const current = particlePositionsRef.current[i] || {};
          p.x = current.x || p.x;
          p.y = current.y || p.y;
          p.size = current.size || p.size;
          p.targetX = p.originalX;
          p.targetY = p.originalY;
          p.targetSize = p.originalSize;
          p.animatingIn = true;
          p.animationProgress = 0;
        });
      }
    }
  }, [isControlled, visible]);

  useEffect(() => {
    if (!containerRef.current) return;

    const animateParticles = () => {
      if (!containerRef.current) return;

      const ctx = containerRef.current.getContext("2d");
      const { width, height } = containerRef.current;

      ctx.clearRect(0, 0, width, height);

      const bounceMarginX = width * 0.08;
      const bounceMarginY = height * 0.12;
      let particlesAnimatingIn = 0;

      if (isAnimatingOut) {
        explosionTimeRef.current += 0.5;
      }

      particlesRef.current.forEach((p) => {
        let opacity;

        if (isAnimatingOut) {
          if (
            !p.explosionStarted &&
            explosionTimeRef.current > p.explosionDelay
          ) {
            p.explosionStarted = true;
          }

          if (p.explosionStarted) {
            if (p.currentExplosionSpeed < p.explosionMaxSpeed) {
              p.currentExplosionSpeed += p.explosionAcceleration;
            }

            p.x += p.directionX * p.currentExplosionSpeed;
            p.y += p.directionY * p.currentExplosionSpeed;

            p.speedY = p.speedY || 0;
            p.speedY += 0.15;
            p.y += p.speedY;

            p.x += (Math.random() - 0.5) * 1.0;
            p.y += (Math.random() - 0.5) * 1.0;

            opacity = calculateEdgeOpacity(p.x, p.y, width, height) - 0.03;
            if (opacity < 0) opacity = 0;

            if (p.size > 0.2) {
              p.size -= 0.05;
            }
          }
        } else if (isAnimatingIn && p.animatingIn) {
          particlesAnimatingIn++;
          p.animationProgress += 0.035;

          if (p.animationProgress >= 1) {
            p.x = p.originalX;
            p.y = p.originalY;
            p.size = p.originalSize;
            p.speedX = p.originalSpeedX;
            p.speedY = p.originalSpeedY;
            p.animatingIn = false;
          } else {
            const t = p.animationProgress;
            const ease = 1 - Math.pow(1 - t, 3);
            const rate = ease * 0.2;

            p.x = p.x + (p.originalX - p.x) * rate;
            p.y = p.y + (p.originalY - p.y) * rate;
            p.size = Math.min(
              p.originalSize,
              p.size + (p.originalSize - p.size) * rate
            );
          }
          opacity = calculateEdgeOpacity(p.x, p.y, width, height);
        } else if (!isRevealed) {
          p.x += p.speedX * 0.7;
          p.y += p.speedY * 0.7;

          if (p.x <= bounceMarginX) {
            p.x = bounceMarginX;
            p.speedX *= -1;
          } else if (p.x >= width - bounceMarginX) {
            p.x = width - bounceMarginX;
            p.speedX *= -1;
          }

          if (p.y <= bounceMarginY) {
            p.y = bounceMarginY;
            p.speedY *= -1;
          } else if (p.y >= height - bounceMarginY) {
            p.y = height - bounceMarginY;
            p.speedY *= -1;
          }

          p.size = p.originalSize;
          opacity = calculateEdgeOpacity(p.x, p.y, width, height);
        }

        if (opacity > 0) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(230, 230, 230, ${opacity})`;
          ctx.fill();
        }
      });

      if (isAnimatingIn && particlesAnimatingIn === 0) {
        setIsAnimatingIn(false);
      }

      animationRef.current = requestAnimationFrame(animateParticles);
    };

    animateParticles();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRevealed, isAnimatingOut, isAnimatingIn, numberOfParticles]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        container.width = rect.width;
        container.height = rect.height;
        resetParticles();
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [resetParticles]);  // Added resetParticles back as a dependency since it's used inside the effect

  let eventHandlers = {};

  if (revealKind === "hover") {
    eventHandlers = {
      onMouseEnter: handleReveal,
      onMouseLeave: handleHide,
      onTouchStart: handleReveal,
      onTouchEnd: handleHide,
    };
  } else if (revealKind === "click") {
    eventHandlers = {
      onClick: (e) => {
        if (!isControlled) {
          handleReveal();
        } else {
          setVisible(true);
        }
      },
      onMouseLeave: isRevealed ? handleHide : undefined,
      onTouchEnd: isRevealed
        ? (e) => {
            e.stopPropagation();
            handleHide();
          }
        : undefined,
    };
  }

  return (
    <div style={styles.container} {...eventHandlers}>
      <div
        style={{
          ...styles.content,
          ...(isRevealed ? styles.contentRevealed : styles.contentHidden),
        }}
      >
        {children}
      </div>
      <canvas
        ref={containerRef}
        style={{
          ...styles.canvas,
          opacity: isRevealed && !isAnimatingOut ? 0 : 1,
        }}
      />
    </div>
  );
};

export default InvisibleInk;
